import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle2,
  Sparkles,
  Crown,
  Users,
  Video,
  Calendar,
  BookOpen,
  Headphones,
  MessageCircleHeart,
  Loader2,
  ShieldCheck,
  Flame,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUTHOR_IMAGE =
  'https://customer-assets.emergentagent.com/job_e5aeb861-4698-4c3f-a54b-4b0b2b4402c1/artifacts/zvomh038_FB_IMG_1780263257283.jpg';

// What's INCLUDED — the offer stack
const STACK = [
  {
    icon: Video,
    title: 'Weekly Power Hour Recode',
    detail: 'Live, topic-based group Recode session every week — applying the work to real life as it unfolds.',
    value: 297,
  },
  {
    icon: Calendar,
    title: 'Weekly Power Planning Session',
    detail: 'Plan your week from your most magnetic state. Live, guided, and personal.',
    value: 197,
  },
  {
    icon: MessageCircleHeart,
    title: '1-on-1 Recode Access',
    detail: 'Direct line to schedule a private Superconscious Recode Session whenever you need depth work.',
    value: 397,
  },
  {
    icon: BookOpen,
    title: 'Taking Your Power Back E-Book',
    detail: 'The full 5-Step Magnetic Mind Method — yours forever as a Collective member.',
    value: 59,
  },
  {
    icon: Headphones,
    title: 'Daily Power Practice — Audio Library',
    detail: 'Bonus guided meditations (morning + evening) recorded by Breah to anchor the practice every day.',
    value: 147,
  },
  {
    icon: Calendar,
    title: 'Power Planning Guides + Worksheets',
    detail: 'Daily and weekly worksheets, the 30-Day Challenge calendar, and monthly progress trackers.',
    value: 47,
  },
  {
    icon: Sparkles,
    title: 'Live Monthly Trainings + Specials',
    detail: 'Reserved seat for every monthly live training and any special workshops I host inside the Collective.',
    value: 197,
  },
  {
    icon: Users,
    title: 'The Collective — Community',
    detail: 'A private space of souls doing the same work, supporting and witnessing each other.',
    value: 97,
  },
];

const TOTAL_VALUE = STACK.reduce((sum, s) => sum + s.value, 0);

export default function CollectiveLandingPage() {
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/membership/package`);
        if (!cancelled) setPkg(data);
      } catch {
        toast.error('Could not load membership details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const startCheckout = async () => {
    setCheckingOut(true);
    try {
      const { data } = await axios.post(`${API}/membership/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start checkout');
      setCheckingOut(false);
    }
  };

  if (loading || !pkg) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 text-secondary animate-spin" />
      </div>
    );
  }

  const isFounding = pkg.current_tier === 'founding';
  const price = pkg.current_price_usd;
  const regularPrice = pkg.regular_price_usd;
  const spotsLeft = pkg.founding_members_left;
  const urgent = isFounding && spotsLeft <= 10;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="collective-landing-root">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-silver/15">
        <div className="absolute inset-0 bg-gradient-to-br from-maroon/20 via-background to-secondary/10 pointer-events-none" />
        <div className="container relative mx-auto px-4 py-16 md:py-24 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            {isFounding && (
              <Badge
                className={`mb-5 px-4 py-1.5 text-xs uppercase tracking-widest border-secondary/40 ${
                  urgent
                    ? 'bg-maroon text-primary-foreground border-maroon animate-pulse'
                    : 'bg-secondary/15 text-secondary'
                }`}
                data-testid="founding-badge"
              >
                <Flame className="w-3 h-3 mr-1.5 inline" />
                {urgent ? `Only ${spotsLeft} Founding Spots Left` : `Founding Member Pricing · ${spotsLeft} of ${pkg.founding_member_cap} spots open`}
              </Badge>
            )}

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05] mb-5">
              The Power<br className="md:hidden"/>
              <span className="text-secondary"> Collective</span>
            </h1>

            <p className="text-base md:text-lg text-foreground/80 max-w-2xl mx-auto leading-relaxed mb-3">
              Your weekly home for Power Planning, live Recodes, and a circle of people taking their power back together.
            </p>
            <p className="text-sm text-muted-foreground italic mb-8">
              Led personally by Breah Kettunen · Members-only access · Cancel anytime
            </p>

            {/* PRICE BLOCK */}
            <Card className="max-w-md mx-auto p-7 bg-card border-secondary/30 mb-6 shadow-xl shadow-maroon/10" data-testid="price-card">
              {isFounding && (
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Crown className="w-4 h-4 text-secondary" />
                  <span className="text-xs uppercase tracking-widest text-secondary font-semibold">Founding Member Price</span>
                </div>
              )}
              <div className="text-center mb-3">
                <div className="flex items-baseline justify-center gap-2 mb-1">
                  <span className="font-heading text-5xl text-foreground" data-testid="member-price">${price}</span>
                  <span className="text-base text-muted-foreground">/ month</span>
                </div>
                {isFounding && (
                  <p className="text-sm text-muted-foreground">
                    <span className="line-through opacity-60">${regularPrice}/mo</span>
                    {' '}— Yours forever as long as you stay a member.
                  </p>
                )}
              </div>
              <Button
                size="lg"
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold text-base h-12"
                onClick={startCheckout}
                disabled={checkingOut}
                data-testid="checkout-cta"
              >
                {checkingOut ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening Checkout…</>
                ) : (
                  <>Claim My {isFounding ? 'Founding ' : ''}Spot →</>
                )}
              </Button>
              <div className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="w-3.5 h-3.5" />
                Secure checkout · Cancel anytime in one click
              </div>
            </Card>

            <p className="text-xs text-muted-foreground">
              Already a member?{' '}
              <Link to="/circle" className="text-secondary hover:underline">Sign in to the Collective →</Link>
            </p>
          </motion.div>
        </div>
      </section>

      {/* THE OFFER STACK */}
      <section className="border-b border-silver/15 bg-card/30">
        <div className="container mx-auto px-4 py-16 max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest text-secondary font-semibold mb-2">Here&apos;s everything you get</p>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground mb-3">
              The Collective Stack
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto">
              Every month, every week, every day — built so you actually <em>use</em> what you learn.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="stack-grid">
            {STACK.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <Card className="p-5 bg-card border-silver/20 hover:border-secondary/40 transition-colors h-full">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-secondary/15 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-secondary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h3 className="font-heading text-lg text-foreground leading-tight">{item.title}</h3>
                          <Badge variant="outline" className="text-[10px] shrink-0 border-secondary/30 text-secondary">
                            ${item.value} value
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{item.detail}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Value summary */}
          <Card className="max-w-2xl mx-auto p-7 bg-gradient-to-br from-maroon/15 to-secondary/10 border-secondary/30" data-testid="value-summary-card">
            <div className="text-center space-y-2">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Total monthly value</p>
              <p className="font-heading text-4xl text-foreground">
                <span className="line-through opacity-50">${TOTAL_VALUE.toLocaleString()}</span>
              </p>
              <p className="text-sm text-muted-foreground">Your investment as a {isFounding ? 'Founding' : ''} Member:</p>
              <p className="font-heading text-5xl text-secondary">${price}<span className="text-xl text-muted-foreground">/mo</span></p>
              {isFounding && (
                <p className="text-xs text-secondary font-semibold pt-2 border-t border-silver/20 mt-3">
                  ✦ Locked in forever — even as I raise the price for everyone else.
                </p>
              )}
            </div>
          </Card>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="border-b border-silver/15">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <div className="grid md:grid-cols-[260px,1fr] gap-8 items-start">
            <img
              src={AUTHOR_IMAGE}
              alt="Breah Kettunen"
              className="w-full max-w-[260px] mx-auto rounded-xl shadow-xl shadow-maroon/10"
            />
            <div>
              <p className="text-xs uppercase tracking-widest text-secondary font-semibold mb-2">Who this is for</p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground mb-5 leading-tight">
                You&apos;re ready to <em className="text-secondary">live</em> the work, not just read about it.
              </h2>
              <ul className="space-y-3 text-foreground/85">
                {[
                  "You've done the books, the workshops, the journaling — and you're tired of starting over every Monday.",
                  "You want a steady weekly practice that actually changes how you show up — at work, at home, in your own mirror.",
                  "You want access to me, real-time, not buried in a giant program with 500 strangers.",
                  "You want to be witnessed by others doing this same work — without the noise of social media.",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-base">
                    <CheckCircle2 className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-card/30">
        <div className="container mx-auto px-4 py-16 max-w-3xl text-center">
          <p className="text-xs uppercase tracking-widest text-secondary font-semibold mb-3">Final note from Breah</p>
          <h2 className="font-heading text-3xl md:text-4xl text-foreground mb-5 leading-tight">
            I&apos;m only opening 50 founding spots.
          </h2>
          <p className="text-base text-foreground/85 leading-relaxed mb-2">
            Once they&apos;re gone, the price doubles for everyone after. The work inside doesn&apos;t change — but the depth of access and the locked-in pricing? That&apos;s the founding-member promise.
          </p>
          <p className="text-base text-foreground/85 leading-relaxed mb-8">
            If you&apos;ve felt the pull all the way down this page, that&apos;s the answer.
          </p>

          <Card className="max-w-md mx-auto p-6 bg-card border-secondary/40" data-testid="final-cta-card">
            <p className="text-sm text-muted-foreground mb-1">Founding Member · cancel anytime</p>
            <p className="font-heading text-4xl text-foreground mb-4">
              ${price}<span className="text-base text-muted-foreground">/month</span>
            </p>
            <Button
              size="lg"
              className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold text-base h-12"
              onClick={startCheckout}
              disabled={checkingOut}
              data-testid="checkout-cta-bottom"
            >
              {checkingOut ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening Checkout…</>
              ) : (
                <>Join The Power Collective →</>
              )}
            </Button>
          </Card>
        </div>
      </section>
    </div>
  );
}
